import { Menu as MenuPrimitive } from "@base-ui/react/menu"
import { clsx } from "clsx"
import { FA6Icon } from "./FA6Icon"
import styles from "./Menu.module.css"
import "./DropdownMenu.module.css"



function DropdownMenuRoot(props: MenuPrimitive.Root.Props) {
  return <MenuPrimitive.Root {...props} />
}

// A <button> that toggles the menu. Bare passthrough (like Modal.Trigger) — style
// it with render, e.g. <DropdownMenu.Trigger render={<Button />}>.
function DropdownMenuTrigger({ className, ...props }: MenuPrimitive.Trigger.Props) {
  return (
    <MenuPrimitive.Trigger data-slot="dropdown-menu-trigger" className={className} {...props} />
  )
}

// Bundles Portal + Positioner + Popup. Anchored below the trigger by default;
// a nested SubmenuRoot flips its popup to the inline-end automatically.
function DropdownMenuPopup({
  className,
  children,
  side,
  align = "start",
  sideOffset = 4,
  alignOffset,
  ...props
}: MenuPrimitive.Popup.Props &
  Pick<MenuPrimitive.Positioner.Props, "side" | "align" | "sideOffset" | "alignOffset">) {
  return (
    <MenuPrimitive.Portal data-slot="dropdown-menu-portal">
      <MenuPrimitive.Positioner
        data-slot="dropdown-menu-positioner"
        className={styles.positioner}
        side={side}
        align={align}
        sideOffset={sideOffset}
        alignOffset={alignOffset}
      >
        <MenuPrimitive.Popup
          data-slot="dropdown-menu-popup"
          className={clsx(styles.popup, className)}
          {...props}
        >
          {children}
        </MenuPrimitive.Popup>
      </MenuPrimitive.Positioner>
    </MenuPrimitive.Portal>
  )
}

function DropdownMenuItem({ className, ...props }: MenuPrimitive.Item.Props) {
  return (
    <MenuPrimitive.Item
      data-slot="dropdown-menu-item"
      className={clsx(styles.item, className)}
      {...props}
    />
  )
}

// Navigates — renders an <a>; give it href.
function DropdownMenuLinkItem({ className, ...props }: MenuPrimitive.LinkItem.Props) {
  return (
    <MenuPrimitive.LinkItem
      data-slot="dropdown-menu-link-item"
      className={clsx(styles.item, styles.linkItem, className)}
      {...props}
    />
  )
}

function DropdownMenuSeparator({ className, ...props }: MenuPrimitive.Separator.Props) {
  return (
    <MenuPrimitive.Separator
      data-slot="dropdown-menu-separator"
      className={clsx(styles.separator, className)}
      {...props}
    />
  )
}

function DropdownMenuGroup({ className, ...props }: MenuPrimitive.Group.Props) {
  return (
    <MenuPrimitive.Group data-slot="dropdown-menu-group" className={className} {...props} />
  )
}

function DropdownMenuGroupLabel({ className, ...props }: MenuPrimitive.GroupLabel.Props) {
  return (
    <MenuPrimitive.GroupLabel
      data-slot="dropdown-menu-group-label"
      className={clsx(styles.groupLabel, className)}
      {...props}
    />
  )
}

// Keeps the menu open on click by default. The indicator slot reserves its width.
function DropdownMenuCheckboxItem({
  className,
  children,
  ...props
}: MenuPrimitive.CheckboxItem.Props) {
  return (
    <MenuPrimitive.CheckboxItem
      data-slot="dropdown-menu-checkbox-item"
      className={clsx(styles.item, className)}
      {...props}
    >
      <span className={styles.indicator}>
        <MenuPrimitive.CheckboxItemIndicator data-slot="dropdown-menu-checkbox-item-indicator">
          <FA6Icon icon="check" />
        </MenuPrimitive.CheckboxItemIndicator>
      </span>
      <span className={styles.itemText}>{children}</span>
    </MenuPrimitive.CheckboxItem>
  )
}

function DropdownMenuRadioGroup({ className, ...props }: MenuPrimitive.RadioGroup.Props) {
  return (
    <MenuPrimitive.RadioGroup
      data-slot="dropdown-menu-radio-group"
      className={className}
      {...props}
    />
  )
}

function DropdownMenuRadioItem({
  className,
  children,
  ...props
}: MenuPrimitive.RadioItem.Props) {
  return (
    <MenuPrimitive.RadioItem
      data-slot="dropdown-menu-radio-item"
      className={clsx(styles.item, className)}
      {...props}
    >
      <span className={styles.indicator}>
        <MenuPrimitive.RadioItemIndicator data-slot="dropdown-menu-radio-item-indicator">
          <FA6Icon icon="circle" className={styles.radioDot} />
        </MenuPrimitive.RadioItemIndicator>
      </span>
      <span className={styles.itemText}>{children}</span>
    </MenuPrimitive.RadioItem>
  )
}

function DropdownMenuSubmenuRoot(props: MenuPrimitive.SubmenuRoot.Props) {
  return <MenuPrimitive.SubmenuRoot {...props} />
}

// The entry that opens a submenu — an item with a trailing chevron.
function DropdownMenuSubmenuTrigger({
  className,
  children,
  ...props
}: MenuPrimitive.SubmenuTrigger.Props) {
  return (
    <MenuPrimitive.SubmenuTrigger
      data-slot="dropdown-menu-submenu-trigger"
      className={clsx(styles.item, styles.submenuTrigger, className)}
      {...props}
    >
      <span className={styles.itemText}>{children}</span>
      <FA6Icon icon="angle-right" className={styles.submenuChevron} />
    </MenuPrimitive.SubmenuTrigger>
  )
}

const DropdownMenu = {
  Root: DropdownMenuRoot,
  Trigger: DropdownMenuTrigger,
  Popup: DropdownMenuPopup,
  Item: DropdownMenuItem,
  LinkItem: DropdownMenuLinkItem,
  Separator: DropdownMenuSeparator,
  Group: DropdownMenuGroup,
  GroupLabel: DropdownMenuGroupLabel,
  CheckboxItem: DropdownMenuCheckboxItem,
  RadioGroup: DropdownMenuRadioGroup,
  RadioItem: DropdownMenuRadioItem,
  SubmenuRoot: DropdownMenuSubmenuRoot,
  SubmenuTrigger: DropdownMenuSubmenuTrigger,
}

// Associate the menu with external triggers (see Base UI Menu handle).
const createDropdownMenuHandle = MenuPrimitive.createHandle

export { DropdownMenu, createDropdownMenuHandle }
